import React, { useEffect, useState } from "react";
import { X, Tag as TagIcon, Plus, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

// Sprint 8.A.1 — Modal zum Bearbeiten von Tags + Notizen eines gespeicherten Flexpfads.
// Limits: Free = 3 Tags / 1.000 Zeichen Notizen, Pro = 10 / 3.000.
export default function EditBrueckeModal({ open, onClose, item, isPro, onSaved }) {
  const maxTags = isPro ? 10 : 3;
  const maxNotes = isPro ? 3000 : 1000;

  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && item) {
      setTags(Array.isArray(item.tags) ? item.tags : []);
      setNotes(typeof item.notes === "string" ? item.notes : "");
      setTagInput("");
      setError(null);
    }
  }, [open, item]);

  if (!open || !item) return null;

  const handleAddTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (!t) return;
    if (t.length > 30) {
      setError("Tag ist zu lang (max. 30 Zeichen).");
      return;
    }
    if (tags.includes(t)) {
      setTagInput("");
      return;
    }
    if (tags.length >= maxTags) {
      setError(
        isPro
          ? `Maximal ${maxTags} Tags pro Flexpfad.`
          : `Free erlaubt ${maxTags} Tags. Mit Pro Solo: 10 Tags.`,
      );
      return;
    }
    setTags([...tags, t]);
    setTagInput("");
    setError(null);
  };

  const handleRemoveTag = (t) => {
    setTags(tags.filter((x) => x !== t));
    setError(null);
  };

  const handleNotesChange = (e) => {
    const v = e.target.value;
    if (v.length > maxNotes) {
      setNotes(v.slice(0, maxNotes));
      return;
    }
    setNotes(v);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await api.patch(`/anliegen/${item.id}/update`, {
        tags,
        notes,
      });
      if (onSaved) onSaved(res.data?.run || { id: item.id, tags, notes });
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "too_many_tags") {
        setError(`Zu viele Tags (max. ${detail.max}).`);
      } else if (detail?.code === "notes_too_long") {
        setError(`Notizen zu lang (max. ${detail.max} Zeichen).`);
      } else {
        setError(formatApiErrorDetail(detail) || "Speichern fehlgeschlagen.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4"
      onClick={onClose}
      data-testid="edit-bruecke-modal"
    >
      <div
        className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Schließen"
          data-testid="edit-bruecke-close"
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-700"
        >
          <X className="w-5 h-5" />
        </button>
        <h3 className="text-lg font-extrabold tracking-tight text-[var(--fts-text-primary)]">
          Flexpfad bearbeiten
        </h3>
        <p className="mt-1 text-xs text-slate-500 line-clamp-2">
          {item.bridge_text || item.situation || "—"}
        </p>

        {/* Tags */}
        <div className="mt-5">
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-sm font-semibold text-[var(--fts-text-primary)] flex items-center gap-1.5">
              <TagIcon className="w-3.5 h-3.5" /> Tags
            </label>
            <span
              data-testid="edit-bruecke-tags-counter"
              className={`text-xs ${tags.length >= maxTags ? "text-amber-600 font-semibold" : "text-slate-400"}`}
            >
              {tags.length} / {maxTags}
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5 mb-2" data-testid="edit-bruecke-tags-list">
            {tags.map((t) => (
              <span
                key={t}
                data-testid={`edit-bruecke-tag-${t}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs"
              >
                {t}
                <button
                  type="button"
                  onClick={() => handleRemoveTag(t)}
                  aria-label={`Tag ${t} entfernen`}
                  className="text-slate-400 hover:text-red-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddTag();
                }
              }}
              placeholder={tags.length >= maxTags ? "Limit erreicht" : "Neuer Tag (z. B. chef)"}
              disabled={tags.length >= maxTags}
              data-testid="edit-bruecke-tag-input"
              className="flex-1 rounded-md border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--fts-primary-bright)] disabled:bg-slate-50 disabled:text-slate-400"
            />
            <button
              type="button"
              onClick={handleAddTag}
              disabled={tags.length >= maxTags || !tagInput.trim()}
              data-testid="edit-bruecke-tag-add"
              className="inline-flex items-center gap-1 rounded-md bg-[var(--fts-primary-bright)] hover:bg-[var(--fts-primary-deep)] disabled:bg-slate-300 text-white px-3 py-1.5 text-xs font-bold"
            >
              <Plus className="w-3.5 h-3.5" /> Hinzufügen
            </button>
          </div>
          {!isPro && (
            <p className="mt-1.5 text-xs text-slate-400">
              Free: max. 3 Tags · Pro Solo: 10 Tags
            </p>
          )}
        </div>

        {/* Notes */}
        <div className="mt-5">
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-sm font-semibold text-[var(--fts-text-primary)]">
              Notizen
            </label>
            <span
              data-testid="edit-bruecke-notes-counter"
              className={`text-xs ${notes.length >= maxNotes * 0.9 ? "text-amber-600 font-semibold" : "text-slate-400"}`}
            >
              {notes.length} / {maxNotes}
            </span>
          </div>
          <textarea
            value={notes}
            onChange={handleNotesChange}
            rows={5}
            placeholder="Eigene Gedanken, Kontext, was bisher passiert ist…"
            data-testid="edit-bruecke-notes-input"
            className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--fts-primary-bright)] resize-y"
          />
          {!isPro && (
            <p className="mt-1 text-xs text-slate-400">
              Free: 1.000 Zeichen · Pro Solo: 3.000 Zeichen
            </p>
          )}
        </div>

        {error && (
          <p
            data-testid="edit-bruecke-error"
            className="mt-3 text-sm text-red-600"
          >
            {error}
          </p>
        )}

        <div className="mt-6 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="edit-bruecke-cancel"
            className="flex-1 rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            data-testid="edit-bruecke-save"
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md bg-[var(--fts-primary-deep)] hover:opacity-90 text-white px-4 py-2 text-sm font-bold disabled:opacity-50"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
}
